import React from 'react';
import { useRouter } from 'next/router';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import Head from '../Head';
import withQuery from '../../lib/withQuery';
import profileBySlugQuery
  from '../../lib/queries/products/profileBySlugCourses.graphql';
import Courses from './CompanyProfilePages/Courses';
import messages from '../../lib/messages.json';
import CourseList from '../Education/CourseList';
import useNoCacheOnServerFetchPolicy
  from '../../lib/useNoCacheOnServerFetchPolicy';
import {
  coursesListImageConfigurations,
  coursesListLogoImageConfigurations,
  coursesListMobileImageConfigurations,
} from '../../lib/imageConfigurations';

const schoolProfileCoursesPropertyTypes = {
  data: PropTypes.shape({
    profileBySlug: PropTypes.shape({
      id: PropTypes.string.isRequired,
      coursesConnection: PropTypes.shape({
        edges: PropTypes.arrayOf(PropTypes.shape({}).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  fetchMore: PropTypes.func.isRequired,
  variables: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
};

type SchoolProfileCoursesProperties = InferProps<
  typeof schoolProfileCoursesPropertyTypes
>;

const SchoolProfileCourses = ({
  data,
  variables,
  fetchMore,
  loading,
}: SchoolProfileCoursesProperties) => {
  const intl = useIntl();

  const { profileBySlug } = data;
  const { id } = profileBySlug;

  return (
    <>
      <Head
        title={
          intl.formatMessage(messages['schools-directory.title'])
        }
        description={
          intl.formatMessage(messages['schools-directory.description'])
        }
        subjectType="Profile"
        subjectId={id}
      />
      <Courses profile={profileBySlug}>
        <CourseList
          data={data}
          primaryField="profileBySlug"
          connectionField="coursesConnection"
          variables={variables}
          fetchMore={fetchMore}
          loading={loading}
        />
      </Courses>
    </>
  );
};

SchoolProfileCourses.propTypes = schoolProfileCoursesPropertyTypes;

const SchoolProfileCoursesWithQuery = withQuery(SchoolProfileCourses);

const SchoolProfileCoursesPage = () => {
  const { query: { slug } } = useRouter();
  const fetchPolicy = useNoCacheOnServerFetchPolicy();

  return (
    <SchoolProfileCoursesWithQuery
      query={profileBySlugQuery}
      variables={{
        slug,
        first: 10,
        logoConfigurations: coursesListLogoImageConfigurations,
        imageConfigurations: coursesListImageConfigurations,
        mobileImageConfigurations: coursesListMobileImageConfigurations,
      }}
      errorMessage="Error loading the school courses"
      fetchPolicy={fetchPolicy}
    />
  );
};

export default SchoolProfileCoursesPage;
